import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';
import axios from 'axios';
import { API } from '@/config';

// Default localized messages — used as fallback if the admin has NOT set a
// custom message via /api/admin/site/notice. Kept in sync with the app's 9 languages.
const defaultMessages = {
  sv: 'Vi har ett tekniskt fel just nu — vårt team arbetar på att åtgärda det så snabbt som möjligt. Tack för ditt tålamod.',
  ar: 'لدينا مشكلة تقنية حالياً — فريقنا يعمل على إصلاحها في أسرع وقت ممكن. شكراً لصبركم.',
  en: "We're experiencing a technical issue right now — our team is working to fix it as fast as possible. Thank you for your patience.",
  tr: 'Şu anda teknik bir sorun yaşıyoruz — ekibimiz mümkün olan en kısa sürede düzeltmek için çalışıyor. Sabrınız için teşekkürler.',
  fa: 'در حال حاضر با یک مشکل فنی مواجه هستیم — تیم ما در حال تلاش برای رفع آن در سریع‌ترین زمان ممکن است. از صبر شما متشکریم.',
  so: 'Hadda waxaa jirta dhibaato farsamo — kooxdayadu waxay ka shaqaynaysaa hagaajinta si degdeg ah. Dulqaadkaaga waa ku mahadsantahay.',
  pl: 'Mamy obecnie problem techniczny — nasz zespół pracuje nad jego rozwiązaniem tak szybko, jak to możliwe. Dziękujemy za cierpliwość.',
  es: 'Estamos experimentando un problema técnico — nuestro equipo está trabajando para solucionarlo lo más rápido posible. Gracias por su paciencia.',
  de: 'Wir haben gerade ein technisches Problem — unser Team arbeitet daran, es so schnell wie möglich zu beheben. Danke für Ihre Geduld.',
};

const MaintenanceBanner = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language || 'sv';
  const isRTL = lang === 'ar' || lang === 'fa';

  const [notice, setNotice] = useState({ enabled: false, level: 'warning', message: {} });
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(`${API}/site/notice`);
        if (!cancelled) setNotice(res.data || { enabled: false });
      } catch (e) { /* silent */ }
    };
    load();
    // Re-poll every 60s so admin toggles take effect without a full page reload
    const iv = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  useEffect(() => {
    // If admin toggled OFF or updated the notice, un-dismiss for everyone
    const stored = localStorage.getItem('site_notice_dismissed_updated_at');
    if (notice.updated_at && stored !== notice.updated_at) {
      setDismissed(false);
      localStorage.removeItem('site_notice_dismissed_updated_at');
    } else if (stored === notice.updated_at) {
      setDismissed(true);
    }
  }, [notice.updated_at]);

  if (!notice.enabled || dismissed) return null;

  const text = (notice.message && notice.message[lang]) || defaultMessages[lang] || defaultMessages.en;

  const levelStyles = {
    info: 'from-blue-600 via-sky-600 to-blue-600 border-blue-700',
    warning: 'from-amber-600 via-orange-600 to-amber-600 border-amber-700',
    error: 'from-red-600 via-rose-600 to-red-600 border-red-700',
  };
  const gradient = levelStyles[notice.level] || levelStyles.error;

  const handleDismiss = () => {
    setDismissed(true);
    if (notice.updated_at) {
      localStorage.setItem('site_notice_dismissed_updated_at', notice.updated_at);
    }
  };

  return (
    <div
      dir={isRTL ? 'rtl' : 'ltr'}
      data-testid="maintenance-banner"
      className={`relative overflow-hidden bg-gradient-to-r ${gradient} text-white shadow-lg border-b`}
      style={{ zIndex: 60 }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 opacity-20 pointer-events-none"
        style={{
          backgroundImage:
            'repeating-linear-gradient(45deg, rgba(255,255,255,0.4) 0 12px, transparent 12px 24px)',
          animation: 'stripeSlide 1.5s linear infinite',
        }}
      />

      <div className="relative flex items-center gap-3 py-2.5 px-4">
        <span className="flex-shrink-0 relative">
          <span className="absolute inset-0 rounded-full bg-white/30 animate-ping" />
          <AlertTriangle className="w-5 h-5 relative animate-pulse" />
        </span>

        <div className="flex-1 overflow-hidden">
          <div
            className="whitespace-nowrap font-semibold text-sm sm:text-base flex"
            style={{
              animation: `${isRTL ? 'marqueeRTL' : 'marqueeLTR'} 28s linear infinite`,
              minWidth: 'fit-content',
            }}
          >
            <span className="flex-shrink-0 px-6">⚠ {text}</span>
            <span className="flex-shrink-0 px-6" aria-hidden="true">⚠ {text}</span>
            <span className="flex-shrink-0 px-6" aria-hidden="true">⚠ {text}</span>
          </div>
        </div>

        <button
          type="button"
          onClick={handleDismiss}
          data-testid="maintenance-banner-close"
          aria-label="Dismiss"
          className="flex-shrink-0 p-1 rounded-md hover:bg-white/20 active:bg-white/30 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <style>{`
        @keyframes marqueeLTR {
          0%   { transform: translateX(0); }
          100% { transform: translateX(-33.3333%); }
        }
        @keyframes marqueeRTL {
          0%   { transform: translateX(0); }
          100% { transform: translateX(33.3333%); }
        }
        @keyframes stripeSlide {
          0%   { background-position: 0 0; }
          100% { background-position: 34px 0; }
        }
      `}</style>
    </div>
  );
};

export default MaintenanceBanner;
